import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, ChevronLeft } from 'lucide-react';
import { cn } from '@mlm/ui/lib/cn';
import { Composer } from '../components/coach/composer';
import { ConfirmDialog } from '../components/confirm-dialog';
import { ProLock } from '../components/pro-lock';
import { RichText } from '../components/coach/rich-text';
import { SmartActions } from '../components/coach/smart-actions';
import { Block, Chip, CoachButton, Eyebrow, Loading, Screen } from '../components/primitives';
import { apiErrorPayload, coaching, type FeatureState } from '../lib/api';
import {
  deleteKalaConversation,
  getKala,
  KALA_PAGE_SIZE,
  listKalaConversations,
  listKalaMessages,
  sendKalaMessage,
  startKalaConversation,
  type KalaConversation,
  type KalaMessage,
  type KalaPersona,
} from '../lib/assistant-api';
import { instantDay } from '../lib/format';
import {
  qk,
  useEntitlement,
  useFeature,
  useFeatureLabel,
  useTierLabels,
  useToday,
} from '../lib/queries';
import { useFillHeight } from '../lib/use-fill-height';
import { useIsDesktop } from '../lib/use-is-desktop';

const TOOL_NOTES: Record<string, string> = {
  get_today: 'Checking your day',
  get_success_plan: 'Reading your plan',
  get_recent_reviews: 'Reading your reviews',
  get_journal_themes: 'Reading your journal',
  propose_smart_action: 'Preparing a next step',
  save_memory: 'Saving a note',
  search_past_chats: 'Looking through past conversations',
  read_past_chat: 'Reading a past conversation',
};

const ERRORS: Record<string, string> = {
  daily_cap_reached: "You've used today's messages with Master Kala. They reset tomorrow.",
  budget_exhausted: 'Master Kala is resting until next month.',
  credits_exhausted: "You've used your credits for now. Get more to keep talking with Master Kala.",
  coaching_plan_required: 'Conversations with Master Kala are part of a higher plan.',
  assistant_disabled: 'Conversations with Master Kala are switched off right now.',
  assistant_not_configured: 'Master Kala is not set up yet. Please check back soon.',
  persona_not_found: 'Master Kala is not available right now.',
  network_error: 'Connection problem. Check your internet and try again.',
  stream_incomplete: 'The answer was cut off. Ask again to get the rest.',
};
const errorText = (code: string) =>
  ERRORS[code] ?? 'Master Kala could not answer right now. Please try again in a moment.';

const INTRO = "I'm here to help you turn your goals into clear decisions and daily action.";

/** SC-16 and SC-17: conversations with Master Kala, with one-tap smart actions. */
export function CoachPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const desktop = useIsDesktop();
  const qc = useQueryClient();
  const prompts = useFeature('prompt_library');
  const chat = useFeature('kala_chat');
  const kala = useQuery({ queryKey: qk.kala, queryFn: getKala, retry: false, staleTime: 60_000 });
  const conversations = useQuery({
    queryKey: qk.kalaConversations,
    queryFn: listKalaConversations,
    enabled: kala.isSuccess && Boolean(kala.data.persona),
  });
  const [cap, setCap] = useState<number | null>(null);
  useEffect(() => {
    if (kala.data) setCap(kala.data.capRemaining);
  }, [kala.data]);

  const start = useMutation({
    mutationFn: startKalaConversation,
    meta: { errorMessage: 'Could not start a conversation. Please try again.' },
  });
  const open = (first?: string) =>
    start.mutate(undefined, {
      onSuccess: (row) => {
        void qc.invalidateQueries({ queryKey: qk.kalaConversations });
        navigate(`/coach/${row.id}`, { state: first ? { first } : null });
      },
    });

  // A prompt picked from the library with no thread open rides in as a DRAFT
  // (not a `first`, which would send it): open a conversation and carry the
  // draft into its composer, where the [brackets] get filled in.
  const libraryDraft = (location.state as { draft?: string } | null)?.draft;
  const openedForDraft = useRef(false);
  useEffect(() => {
    if (conversationId || !libraryDraft || openedForDraft.current) return;
    if (!kala.isSuccess || !kala.data.persona) return;
    openedForDraft.current = true;
    start.mutate(undefined, {
      onSuccess: (row) => {
        void qc.invalidateQueries({ queryKey: qk.kalaConversations });
        navigate(`/coach/${row.id}`, { replace: true, state: { draft: libraryDraft } });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, libraryDraft, kala.isSuccess]);

  if (kala.isPending) return <Loading />;
  const unavailable = kala.isError
    ? errorText(apiErrorPayload(kala.error)?.error ?? 'network_error')
    : !kala.data.persona
      ? errorText('persona_not_found')
      : null;
  if (unavailable) {
    return (
      <Screen eyebrow="Coach" title="Master Kala" subtitle="Your success coach.">
        <Block title={INTRO}>{unavailable}</Block>
      </Screen>
    );
  }
  // The conversation is a plan feature (Premium at Miracles Protocol): a
  // member whose plan lacks it sees the lock here, never a composer that the
  // server would refuse.
  if (chat !== 'on') {
    return (
      <Screen eyebrow="Coach" title="Master Kala" subtitle="Your success coach.">
        {chat === 'locked' ? (
          <ProLock feature="kala_chat" />
        ) : (
          <Block title={INTRO}>Master Kala is not part of this app right now.</Block>
        )}
      </Screen>
    );
  }
  const persona = kala.data!.persona!;
  const list = (
    <ConversationList
      conversations={conversations.data ?? []}
      loading={conversations.isPending}
      activeId={conversationId ?? null}
      onNew={() => open()}
      starting={start.isPending}
      gold={!desktop}
    />
  );
  const intro = (
    <Intro persona={persona} onAsk={(q) => open(q)} busy={start.isPending} prompts={prompts} />
  );
  const thread = conversationId ? (
    <Thread
      key={conversationId}
      conversationId={conversationId}
      title={conversations.data?.find((c) => c.id === conversationId)?.title ?? null}
      persona={persona}
      cap={cap}
      onCap={setCap}
      desktop={desktop}
      prompts={prompts}
    />
  ) : null;

  if (desktop) {
    return (
      <Screen wide eyebrow="Coach" title="Master Kala" subtitle="Your success coach.">
        <div className="-mb-16 lg:grid lg:grid-cols-[250px_minmax(0,1fr)]">
          <div className="border-r border-border">{list}</div>
          <div className="min-w-0">{thread ?? intro}</div>
        </div>
      </Screen>
    );
  }
  if (thread) {
    return (
      <div className="mx-auto -mb-24 w-full max-w-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <Link to="/coach" className="eyebrow inline-flex items-center gap-1 hover:text-ink">
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Conversations
          </Link>
          {prompts === 'off' ? (
            <span className="eyebrow">Master Kala</span>
          ) : (
            <PromptsLink from={`/coach/${conversationId}`} state={prompts} />
          )}
        </div>
        {thread}
      </div>
    );
  }
  return (
    <Screen eyebrow="Coach" title="Master Kala" subtitle="Your success coach.">
      {intro}
      {list}
    </Screen>
  );
}

/** The way into the prompt library, from wherever the member is; back lands them here again. */
function PromptsLink({
  from,
  state,
  className,
}: {
  from: string;
  state: FeatureState;
  className?: string;
}) {
  const plan = useFeatureLabel('prompt_library');
  return (
    <Link
      to="/coach/prompts"
      state={{ from }}
      className={cn('eyebrow inline-flex items-center gap-1.5 hover:text-ink', className)}
    >
      <BookOpen className="h-3.5 w-3.5" aria-hidden />
      Prompts{state === 'locked' ? ` · ${plan}` : ''}
    </Link>
  );
}

function Intro({
  persona,
  onAsk,
  busy,
  prompts,
}: {
  persona: KalaPersona;
  onAsk: (question: string) => void;
  busy: boolean;
  prompts: FeatureState;
}) {
  return (
    <section className="border-b border-border px-5 py-6 lg:py-8">
      <h2 className="font-display text-[1.5rem] font-semibold leading-[1.2] text-ink lg:text-[1.75rem]">
        {INTRO}
      </h2>
      <p className="mt-2 text-[0.95rem] leading-relaxed text-ink/70">
        Ask me anything about your goals, mindset, challenges, or next steps.
      </p>
      {persona.suggestedQuestions.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {persona.suggestedQuestions.map((q) => (
            <Chip key={q} selected={false} onClick={() => !busy && onAsk(q)}>
              {q}
            </Chip>
          ))}
        </div>
      )}
      {prompts !== 'off' && <PromptsLink from="/coach" state={prompts} className="mt-5" />}
    </section>
  );
}

function ConversationList({
  conversations,
  loading,
  activeId,
  onNew,
  starting,
  gold,
}: {
  conversations: KalaConversation[];
  loading: boolean;
  activeId: string | null;
  onNew: () => void;
  starting: boolean;
  gold: boolean;
}) {
  return (
    <div>
      <div className="px-5 py-4">
        <CoachButton kind={gold ? 'gold' : 'outline'} block onClick={onNew} disabled={starting}>
          New conversation
        </CoachButton>
      </div>
      <Eyebrow className="border-b border-border px-5 pb-3 pt-2">Conversations</Eyebrow>
      {loading ? (
        <p className="eyebrow animate-pulse px-5 py-4">Loading</p>
      ) : conversations.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">No conversations yet.</p>
      ) : (
        <ul>
          {conversations.map((c) => (
            <li key={c.id}>
              <Link
                to={`/coach/${c.id}`}
                aria-current={c.id === activeId ? 'page' : undefined}
                className={cn(
                  'block border-b border-border px-5 py-3.5 transition-colors hover:bg-surface',
                  c.id === activeId && 'bg-surface',
                )}
              >
                <span className="block truncate text-[0.95rem] text-ink">
                  {c.title ?? 'New conversation'}
                </span>
                <span className="eyebrow mt-1 block">
                  {instantDay(c.lastMessageAt ?? c.createdAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Thread({
  conversationId,
  title,
  persona,
  cap,
  onCap,
  desktop,
  prompts,
}: {
  conversationId: string;
  title: string | null;
  persona: KalaPersona;
  cap: number | null;
  onCap: (cap: number | null) => void;
  desktop: boolean;
  prompts: FeatureState;
}) {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const today = useToday();
  const entitlement = useEntitlement();
  const labels = useTierLabels();
  const paneRef = useRef<HTMLDivElement>(null);
  const height = useFillHeight(paneRef, desktop ? 24 : 0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // A prompt from the library arrives as a draft in router state: it fills
  // the box and is NOT sent, so the [brackets] can be edited first.
  const seededDraft = (location.state as { draft?: string } | null)?.draft ?? '';
  const [draft, setDraft] = useState(seededDraft);
  const [pending, setPending] = useState<string | null>(null);
  const [streamText, setStreamText] = useState<string | null>(null);
  const [toolNote, setToolNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const messagesQ = useInfiniteQuery({
    queryKey: qk.kalaThread(conversationId),
    queryFn: ({ pageParam }) => listKalaMessages(conversationId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.length === KALA_PAGE_SIZE ? last.at(-1)?.createdAt : undefined,
  });
  const messages = useMemo(
    () => (messagesQ.data?.pages ?? []).flat().slice().reverse(),
    [messagesQ.data],
  );

  const send = useMutation({
    mutationFn: (body: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      return sendKalaMessage(
        conversationId,
        body,
        {
          onDelta: (text) => {
            setToolNote(null);
            setStreamText((prev) => (prev ?? '') + text);
          },
          onTool: (name) => setToolNote(TOOL_NOTES[name] ?? 'Thinking'),
        },
        controller.signal,
      );
    },
    onSuccess: async (result) => {
      if (result.ok) {
        onCap(result.capRemaining);
        setDraft('');
      } else if (result.error !== 'stopped') {
        setError(result.error);
      }
      // The server saves the member's turn before calling the model, so refetch either way.
      await qc.invalidateQueries({ queryKey: qk.kalaThread(conversationId) });
      void qc.invalidateQueries({ queryKey: qk.kalaConversations });
      setPending(null);
      setStreamText(null);
      setToolNote(null);
      abortRef.current = null;
    },
  });

  const sendText = (raw: string) => {
    const body = raw.trim();
    if (!body || send.isPending) return;
    setPending(body);
    setStreamText(null);
    setError(null);
    coaching.event('coach_message_sent');
    send.mutate(body);
  };

  // The draft is consumed once: clear the state so a refresh does not re-seed it.
  const draftSeeded = useRef(false);
  useEffect(() => {
    if (draftSeeded.current) return;
    draftSeeded.current = true;
    if (seededDraft) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A question picked from the intro rides in router state: send it once, then clear it.
  const firstSent = useRef(false);
  useEffect(() => {
    const first = (location.state as { first?: string } | null)?.first;
    if (!first || firstSent.current || !messagesQ.isSuccess) return;
    firstSent.current = true;
    navigate(location.pathname, { replace: true, state: null });
    sendText(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesQ.isSuccess]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, pending, streamText]);

  const remove = useMutation({
    mutationFn: () => deleteKalaConversation(conversationId),
    meta: { successMessage: 'Conversation deleted.' },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.kalaConversations });
      navigate('/coach', { replace: true });
    },
  });

  const day = today.data?.day ?? null;
  return (
    <div ref={paneRef} className="flex min-h-0 flex-col" style={height ? { height } : undefined}>
      {desktop && (
        <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-3">
          <span className="truncate text-sm text-ink/80">{title ?? 'New conversation'}</span>
          <div className="flex shrink-0 items-center gap-5">
            {prompts !== 'off' && <PromptsLink from={`/coach/${conversationId}`} state={prompts} />}
            <CoachButton
              kind="quiet"
              disabled={remove.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              Delete conversation
            </CoachButton>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this conversation?"
        body="The whole thread goes. Anything Master Kala saved about you is kept."
        confirmLabel="Delete it"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
      <div
        className="min-h-0 flex-1 overflow-y-auto px-5 py-6"
        role="log"
        aria-live="polite"
        aria-label="Conversation with Master Kala"
      >
        {messagesQ.hasNextPage && (
          <div className="mb-6">
            <CoachButton
              kind="quiet"
              disabled={messagesQ.isFetchingNextPage}
              onClick={() => void messagesQ.fetchNextPage()}
            >
              Show earlier messages
            </CoachButton>
          </div>
        )}
        {messagesQ.isPending ? (
          <Loading />
        ) : messages.length === 0 && !pending ? (
          <div>
            <p className="text-[0.95rem] leading-relaxed text-ink/70">
              Ask me anything about your goals, mindset, challenges, or next steps.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {persona.suggestedQuestions.map((q) => (
                <Chip key={q} selected={false} onClick={() => sendText(q)}>
                  {q}
                </Chip>
              ))}
            </div>
            {prompts !== 'off' && (
              <PromptsLink from={`/coach/${conversationId}`} state={prompts} className="mt-5" />
            )}
          </div>
        ) : (
          <ol className="space-y-7">
            {messages.map((m) => (
              <MessageItem key={m.id} message={m} day={day} />
            ))}
          </ol>
        )}
        {pending && (
          <div className="mt-7 space-y-7">
            <MemberText text={pending} />
            <div className="max-w-[92%]">
              <Eyebrow className="mb-2">Master Kala</Eyebrow>
              {streamText ? (
                <div className="text-[0.95rem] leading-relaxed text-ink/90">
                  <RichText text={streamText} />
                </div>
              ) : null}
              {(toolNote || !streamText) && (
                <p className="eyebrow mt-2 animate-pulse">{toolNote ?? 'Thinking'}</p>
              )}
            </div>
          </div>
        )}
        {error && (
          <p role="alert" className="mt-6 text-sm leading-relaxed text-destructive">
            {errorText(error)}
          </p>
        )}
        <div ref={bottomRef} />
      </div>
      <Composer
        value={draft}
        onChange={setDraft}
        onSend={() => sendText(draft)}
        onStop={() => abortRef.current?.abort()}
        sending={send.isPending}
        capRemaining={cap}
        autoFocus={Boolean(seededDraft)}
        creditsNote={
          entitlement.data?.credits &&
          (entitlement.data.credits.costs.find((c) => c.key === 'kala_message')?.cost ?? 0) > 0 ? (
            <>
              {entitlement.data.credits.total}{' '}
              {entitlement.data.credits.total === 1 ? 'credit' : 'credits'} left
              {(entitlement.data.credits.costs.find((c) => c.key === 'kala_message')?.cost ?? 1) > 1
                ? ` · ${entitlement.data.credits.costs.find((c) => c.key === 'kala_message')!.cost} per message`
                : ''}
              {' · '}
              <Link to="/credits" className="text-gold underline underline-offset-4">
                {entitlement.data.credits.total > 0 ? 'See credits' : 'Get more'}
              </Link>
            </>
          ) : undefined
        }
        limitNote={
          entitlement.data?.tier === 'free' && entitlement.data.proDailyMessages ? (
            <>
              {' '}
              {labels.pro} gives you {entitlement.data.proDailyMessages} a day.{' '}
              <Link to="/upgrade" className="text-gold underline underline-offset-4">
                See {labels.pro}
              </Link>
            </>
          ) : undefined
        }
      />
    </div>
  );
}

function MemberText({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap break-words border border-[rgba(16,32,26,0.22)] bg-surface px-4 py-3 text-[0.95rem] leading-relaxed text-ink">
        {text}
      </div>
    </div>
  );
}

function MessageItem({ message, day }: { message: KalaMessage; day: string | null }) {
  if (message.role === 'user') {
    return (
      <li>
        <MemberText text={message.body} />
      </li>
    );
  }
  const meta = message.metadata ?? {};
  return (
    <li className="max-w-[92%]">
      <Eyebrow className="mb-2">Master Kala</Eyebrow>
      <div className="text-[0.95rem] leading-relaxed text-ink/90">
        <RichText text={message.body} />
      </div>
      {meta.truncated && (
        <p className="mt-2 text-xs text-muted-foreground">
          The answer was cut short. Ask Master Kala to continue.
        </p>
      )}
      {meta.smartActions?.length ? <SmartActions actions={meta.smartActions} today={day} /> : null}
    </li>
  );
}
